import { z } from "zod";

// URL state for the Operations dashboard (business days only). Period + categorical filters drive the summary;
// the `d*` keys describe the open drill-through drawer (empty = closed) so a drilled view is a
// shareable link. Filters are arrays to match the API, even though the slicer bar picks one value.

export const operationsPeriod = z.enum(["today", "wtd"]);
export const operationsAttention = z.enum(["clear", "stuck", "blocked", "stuckAndBlocked", "unknown"]);
export const operationsStage = z.enum([
  "requestProcessing",
  "releaseToSubmit",
  "submitToApprove",
  "awaitingClose",
]);
export const operationsFlow = z.enum(["open", "started", "ended"]);
export const operationsDayKind = z.enum(["arrival", "departure"]);
export const operationsStuckBy = z.enum(["pendingTask", "workOrder"]);
/** The start-date tiles' cohort (2026-10-24), on the journey drawer; also the request drawer's chips. */
export const operationsStartRisk = z.enum(["none", "onTime", "atRisk", "breached"]);
export const operationsAgeColor = z.enum(["workType", "attention"]);
/** Attention lens: look at the open work through one state (never "unknown", which is a data condition). */
export const operationsLens = z.enum(["stuck", "blocked", "stuckAndBlocked", "clear"]);
/** Who holds the pen (2026-10-28): the second lens. */
export const operationsPen = z.enum(["internal", "external"]);
export const operationsItemsSort = z.enum([
  "worker", "client", "type", "stage", "age", "pendingFor", "attention", "pendingWith", "owner", "startLead",
]);

export const operationsSchema = z.object({
  period: operationsPeriod.default("wtd"),
  /** Age card colouring: by work-type group (default) or by attention (Stuck or Blocked / Clear). */
  ageColor: operationsAgeColor.default("workType"),
  /**
   * Attention lens (a slicer, not a drill: it survives the drawer). Re-cuts Flow open-now, the stage
   * bars, the age card and the owner table over one state; started / ended, arrivals and the tiles
   * stay on the full population.
   */
  lens: operationsLens.optional(),
  /** Who holds the pen: Internal / External, combinable with the attention lens; survives the drawer like it. */
  pen: operationsPen.optional(),

  // Categorical filters (same names as the API query).
  onboardingsOnly: z.boolean().default(false),
  redeployment: z.boolean().default(false),
  requestType: z.array(z.string()).default([]),
  client: z.array(z.string()).default([]),
  lob: z.array(z.string()).default([]),
  eng: z.array(z.string()).default([]),
  assigned: z.array(z.string()).default([]),
  team: z.array(z.string()).default([]),
  division: z.array(z.string()).default([]),
  manager: z.array(z.string()).default([]),
  country: z.array(z.string()).default([]),

  // Drill-through drawer.
  dAttention: operationsAttention.optional(),
  dStage: operationsStage.optional(),
  dStatus: z.string().optional(),
  dOwner: z.string().optional(),
  // The owner table's team / division rows (2026-10-26); with dJourneyFlow they preset the journey drawer's local filter.
  dTeam: z.string().optional(),
  dDivision: z.string().optional(),
  dManager: z.string().optional(),
  dAgeBin: z.number().int().positive().optional(),
  dWorkType: z.string().optional(),
  dFlow: operationsFlow.optional(),
  dDay: z.string().optional(),
  dDayKind: operationsDayKind.optional(),
  dStuckBy: operationsStuckBy.optional(),
  /** The intake drawer (from the stage card's Request (Intake) row): the open intakes. Its chips and search are local to the drawer. */
  dIntakeFlow: z.enum(["open"]).optional(),
  /** The journey drawer (from the Flow card's Onboarding / Redeployment row, with dWorkType): which cohort it lists. */
  dJourneyFlow: z.enum(["open", "started", "ended"]).optional(),
  /** The start-date tiles (At risk / Breached): the journey drawer on that cohort, with dJourneyFlow=open. */
  dStartRisk: operationsStartRisk.optional(),

  // Drawer view inside the drilled bucket: sort, Attention / Pending-with chips, search. Reset
  // whenever the drawer closes or another number is drilled.
  dSort: operationsItemsSort.optional(),
  dDir: z.enum(["asc", "desc"]).optional(),
  dfAttention: z.array(operationsAttention).default([]),
  dfPending: z.array(z.string()).default([]),
  dfWorkType: z.array(z.string()).default([]),
  dfStartRisk: z.array(operationsStartRisk).default([]),
  dfOwner: z.array(z.string()).default([]),
  dfTeam: z.array(z.string()).default([]),
  dfDivision: z.array(z.string()).default([]),
  dfManager: z.array(z.string()).default([]),
  dfEngagement: z.array(z.string()).default([]),
  dfHolder: z.array(operationsPen).default([]),
  dfQ: z.string().optional(),
});

export type OperationsState = z.infer<typeof operationsSchema>;
export const OPERATIONS_DEFAULTS = operationsSchema.parse({});

export const DRILL_KEYS = [
  "dAttention",
  "dStage",
  "dStatus",
  "dOwner",
  "dTeam",
  "dDivision",
  "dManager",
  "dAgeBin",
  "dWorkType",
  "dFlow",
  "dDay",
  "dDayKind",
  "dStuckBy",
  "dIntakeFlow",
  "dJourneyFlow",
  "dStartRisk",
] as const;
export type DrillKey = (typeof DRILL_KEYS)[number];

/** The drawer is open when any drill key is set. */
export function hasDrill(state: OperationsState): boolean {
  return DRILL_KEYS.some((k) => state[k] !== undefined);
}

export type DrawerViewKey = "dSort" | "dDir" | "dfAttention" | "dfPending" | "dfWorkType" | "dfStartRisk" | "dfOwner" | "dfTeam" | "dfDivision" | "dfManager" | "dfEngagement" | "dfHolder" | "dfQ";

/** A partial state that clears every drill key and the drawer view. */
export function clearedDrill(): Pick<OperationsState, DrillKey | DrawerViewKey> {
  return {
    dSort: undefined,
    dDir: undefined,
    dfAttention: [],
    dfPending: [],
    dfWorkType: [],
    dfStartRisk: [],
    dfOwner: [],
    dfTeam: [],
    dfDivision: [],
    dfManager: [],
    dfEngagement: [],
    dfHolder: [],
    dfQ: undefined,
    dAttention: undefined,
    dStage: undefined,
    dStatus: undefined,
    dOwner: undefined,
    dTeam: undefined,
    dDivision: undefined,
    dManager: undefined,
    dAgeBin: undefined,
    dWorkType: undefined,
    dFlow: undefined,
    dDay: undefined,
    dDayKind: undefined,
    dStuckBy: undefined,
    dIntakeFlow: undefined,
    dJourneyFlow: undefined,
    dStartRisk: undefined,
  };
}
